import type {
  DetailedCircuitFeature,
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
} from '../types/index.js';

import { deriveGeometricApexPoint, lineMidpoint } from './detailedCircuitGeometry.js';
import type { AnalysisGpsPoint as MyChronGpsPoint } from './analysisCore.js';

/** Max distance from lap GPS to a circuit turn feature before we treat the turn as skipped. */
export const TURN_TRAVERSE_MAX_DISTANCE_M = 22;
const TURN_BOUNDARY_ALIGNMENT_MAX_DISTANCE_M = 8;

export type TurnPhaseMatch = {
  featureId: string;
  label: string;
  turnNumber: number;
  entryMs: number;
  apexMs: number;
  exitMs: number;
  wrapsLap: boolean;
  boundariesSwapped: boolean;
};

function localMeters(point: TrackContributionGpsCoord, reference: TrackContributionGpsCoord) {
  const latScale = 111_320;
  const lonScale = latScale * Math.cos((reference.lat * Math.PI) / 180);
  return {
    x: (point.lon - reference.lon) * lonScale,
    y: (point.lat - reference.lat) * latScale,
  };
}

function distanceToLineSquared(
  point: TrackContributionGpsCoord,
  line: TrackContributionGpsCoord[],
) {
  if (line.length === 0) {
    return Number.POSITIVE_INFINITY;
  }
  if (line.length === 1) {
    const local = localMeters(point, line[0]!);
    return local.x ** 2 + local.y ** 2;
  }
  let best = Number.POSITIVE_INFINITY;
  for (let index = 1; index < line.length; index += 1) {
    const reference = line[index - 1]!;
    const target = localMeters(point, reference);
    const end = localMeters(line[index]!, reference);
    const lengthSquared = end.x ** 2 + end.y ** 2;
    const fraction = lengthSquared
      ? Math.max(0, Math.min(1, (target.x * end.x + target.y * end.y) / lengthSquared))
      : 0;
    const deltaX = target.x - end.x * fraction;
    const deltaY = target.y - end.y * fraction;
    best = Math.min(best, deltaX ** 2 + deltaY ** 2);
  }
  return best;
}

function distanceToLineMeters(point: TrackContributionGpsCoord, line: TrackContributionGpsCoord[]) {
  return Math.sqrt(distanceToLineSquared(point, line));
}

function distanceBetweenMeters(left: TrackContributionGpsCoord, right: TrackContributionGpsCoord) {
  const local = localMeters(right, left);
  return Math.sqrt(local.x ** 2 + local.y ** 2);
}

function pointInRing(point: TrackContributionGpsCoord, ring: TrackContributionGpsCoord[]) {
  let inside = false;
  for (
    let index = 0, previous = ring.length - 1;
    index < ring.length;
    previous = index, index += 1
  ) {
    const currentPoint = ring[index]!;
    const previousPoint = ring[previous]!;
    const crosses =
      currentPoint.lat > point.lat !== previousPoint.lat > point.lat &&
      point.lon <
        ((previousPoint.lon - currentPoint.lon) * (point.lat - currentPoint.lat)) /
          (previousPoint.lat - currentPoint.lat) +
          currentPoint.lon;
    if (crosses) inside = !inside;
  }
  return inside;
}

function distanceToFeatureAreaMeters(
  point: TrackContributionGpsCoord,
  feature: DetailedCircuitFeature,
) {
  const rings = featureBoundaryRings(feature);
  if (rings.some((ring) => ring.length >= 3 && pointInRing(point, ring))) return 0;
  const distances = rings
    .filter((ring) => ring.length >= 2)
    .map((ring) => distanceToLineMeters(point, [...ring, ring[0]!]));
  return distances.length ? Math.min(...distances) : 0;
}

function nearestLineIndex(gps: MyChronGpsPoint[], line: TrackContributionGpsCoord[]) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  gps.forEach((point, index) => {
    const distance = distanceToLineSquared(point, line);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

type BoundaryCrossing = {
  index: number;
  progress: number;
  timecodeMs: number;
  coordinate: TrackContributionGpsCoord;
};

function segmentIntersectionFraction(
  previous: MyChronGpsPoint,
  current: MyChronGpsPoint,
  lineStart: TrackContributionGpsCoord,
  lineEnd: TrackContributionGpsCoord,
  strictEndpoints: boolean,
) {
  const origin = lineStart;
  const start = localMeters(previous, origin);
  const end = localMeters(current, origin);
  const boundaryEnd = localMeters(lineEnd, origin);
  const routeX = end.x - start.x;
  const routeY = end.y - start.y;
  const boundaryX = boundaryEnd.x;
  const boundaryY = boundaryEnd.y;
  const denominator = routeX * boundaryY - routeY * boundaryX;
  if (Math.abs(denominator) < 1e-6) return null;

  const routeFraction = (-start.x * boundaryY + start.y * boundaryX) / denominator;
  const boundaryFraction = (-start.x * routeY + start.y * routeX) / denominator;
  const boundaryMargin = strictEndpoints ? 0 : 0.15;
  if (
    routeFraction < 0 ||
    routeFraction > 1 ||
    boundaryFraction < -boundaryMargin ||
    boundaryFraction > 1 + boundaryMargin
  )
    return null;
  return routeFraction;
}

function boundaryCrossings(gps: MyChronGpsPoint[], line: TrackContributionGpsCoord[], strictEndpoints = false) {
  const crossings: BoundaryCrossing[] = [];
  for (let gpsIndex = 1; gpsIndex < gps.length; gpsIndex += 1) {
    const previous = gps[gpsIndex - 1]!;
    const current = gps[gpsIndex]!;
    for (let lineIndex = 1; lineIndex < line.length; lineIndex += 1) {
      const fraction = segmentIntersectionFraction(
        previous,
        current,
        line[lineIndex - 1]!,
        line[lineIndex]!,
        strictEndpoints,
      );
      if (fraction === null) continue;
      crossings.push({
        index: fraction <= 0.5 ? gpsIndex - 1 : gpsIndex,
        progress: gpsIndex - 1 + fraction,
        timecodeMs: previous.timecodeMs + fraction * (current.timecodeMs - previous.timecodeMs),
        coordinate: {
          lat: previous.lat + fraction * (current.lat - previous.lat),
          lon: previous.lon + fraction * (current.lon - previous.lon),
        },
      });
      break;
    }
  }
  return crossings;
}

function projectPointOntoGps(
  gps: MyChronGpsPoint[],
  target: TrackContributionGpsCoord,
): BoundaryCrossing {
  let best: BoundaryCrossing = {
    index: 0,
    progress: 0,
    timecodeMs: gps[0]!.timecodeMs,
    coordinate: { lat: gps[0]!.lat, lon: gps[0]!.lon },
  };
  let bestDistanceSquared = Number.POSITIVE_INFINITY;

  for (let index = 1; index < gps.length; index += 1) {
    const previous = gps[index - 1]!;
    const current = gps[index]!;
    const targetLocal = localMeters(target, previous);
    const currentLocal = localMeters(current, previous);
    const segmentLengthSquared = currentLocal.x ** 2 + currentLocal.y ** 2;
    const fraction =
      segmentLengthSquared > 0
        ? Math.max(
            0,
            Math.min(
              1,
              (targetLocal.x * currentLocal.x + targetLocal.y * currentLocal.y) /
                segmentLengthSquared,
            ),
          )
        : 0;
    const deltaX = targetLocal.x - currentLocal.x * fraction;
    const deltaY = targetLocal.y - currentLocal.y * fraction;
    const distanceSquared = deltaX ** 2 + deltaY ** 2;
    if (distanceSquared >= bestDistanceSquared) continue;

    bestDistanceSquared = distanceSquared;
    best = {
      index: fraction <= 0.5 ? index - 1 : index,
      progress: index - 1 + fraction,
      timecodeMs: previous.timecodeMs + fraction * (current.timecodeMs - previous.timecodeMs),
      coordinate: {
        lat: previous.lat + fraction * (current.lat - previous.lat),
        lon: previous.lon + fraction * (current.lon - previous.lon),
      },
    };
  }

  return best;
}

export function alignDetailedTurnBoundaries(
  geometry: DetailedCircuitGeometry,
): DetailedCircuitGeometry {
  const corners = geometry.features.filter((feature) => feature.type.toLowerCase() === 'corner');
  const targets = corners.flatMap((feature) => {
    const apex = feature.apexPoint ?? feature.directionalPoint;
    return apex ? [{ feature, apex }] : [];
  });
  const donors = corners.filter((feature) => featureBoundaryRings(feature).length > 0);
  if (targets.length < 2 || donors.length < 2) return geometry;

  // A valid authored turn keeps its apex on (or very close to) its own saved
  // surface. Reserve those bundles first so an overlapping neighbouring turn
  // can never steal them. Older reversed layouts can have every surface bundle
  // attached to the reverse-numbered apex; repair only those clear outliers.
  const ownDistanceByTarget = new Map(
    targets.map((target) => [
      target.feature.id,
      distanceToFeatureAreaMeters(target.apex, target.feature),
    ]),
  );
  const alignedTargetIds = new Set(
    targets
      .filter(
        (target) =>
          (ownDistanceByTarget.get(target.feature.id) ?? Number.POSITIVE_INFINITY) <=
          TURN_TRAVERSE_MAX_DISTANCE_M,
      )
      .map((target) => target.feature.id),
  );
  const unresolvedTargets = targets.filter((target) => !alignedTargetIds.has(target.feature.id));
  const availableDonors = donors.filter((donor) => !alignedTargetIds.has(donor.id));

  const candidates = unresolvedTargets
    .flatMap((target) =>
      availableDonors.map((donor) => ({
        target,
        donor,
        distanceM: distanceToFeatureAreaMeters(target.apex, donor),
      })),
    )
    .filter((candidate) => candidate.distanceM <= TURN_BOUNDARY_ALIGNMENT_MAX_DISTANCE_M)
    .sort((left, right) => left.distanceM - right.distanceM);
  const assignedTargets = new Set<string>();
  const assignedDonors = new Set<string>();
  const donorByTarget = new Map<string, DetailedCircuitFeature>();
  for (const candidate of candidates) {
    if (assignedTargets.has(candidate.target.feature.id) || assignedDonors.has(candidate.donor.id))
      continue;
    assignedTargets.add(candidate.target.feature.id);
    assignedDonors.add(candidate.donor.id);
    donorByTarget.set(candidate.target.feature.id, candidate.donor);
  }

  return {
    ...geometry,
    features: geometry.features.map((feature) => {
      if (feature.type.toLowerCase() !== 'corner') return feature;
      const donor = donorByTarget.get(feature.id);
      if (!donor || donor.id === feature.id) return feature;
      const ownApex = feature.apexPoint ?? feature.directionalPoint;
      if (!ownApex) return feature;
      const ownDistance = ownDistanceByTarget.get(feature.id) ?? Number.POSITIVE_INFINITY;
      const donorDistance = distanceToFeatureAreaMeters(ownApex, donor);
      if (
        ownDistance <= TURN_TRAVERSE_MAX_DISTANCE_M ||
        donorDistance > TURN_BOUNDARY_ALIGNMENT_MAX_DISTANCE_M ||
        donorDistance >= ownDistance
      )
        return feature;
      return {
        ...feature,
        polygonRings: donor.polygonRings,
        bounds: donor.bounds,
        entryLine: donor.entryLine,
        exitLine: donor.exitLine,
      };
    }),
  };
}

function featureBoundaryRings(feature: DetailedCircuitFeature) {
  const rings = feature.polygonRings?.length ? feature.polygonRings : (feature.bounds ?? []);
  return rings
    .filter((ring) => ring.length >= 3)
    .map((ring) => {
      const first = ring[0]!;
      const last = ring.at(-1)!;
      return first.lat === last.lat && first.lon === last.lon ? ring : [...ring, first];
    });
}

function resolveFeatureAreaCrossings(
  gps: MyChronGpsPoint[],
  feature: DetailedCircuitFeature,
  apex: BoundaryCrossing,
  strictEndpoints: boolean,
) {
  const crossings = featureBoundaryRings(feature).flatMap((ring) => boundaryCrossings(gps, ring, strictEndpoints));
  if (crossings.length < 2) return null;
  const before =
    crossings
      .filter((crossing) => crossing.timecodeMs <= apex.timecodeMs)
      .sort((left, right) => right.timecodeMs - left.timecodeMs)[0] ?? null;
  const after =
    crossings
      .filter((crossing) => crossing.timecodeMs >= apex.timecodeMs)
      .sort((left, right) => left.timecodeMs - right.timecodeMs)[0] ?? null;
  if (before && after && before.timecodeMs < after.timecodeMs) {
    return { entry: before, exit: after };
  }

  const edgeThreshold = Math.max(2, Math.floor(gps.length * 0.15));
  if (apex.progress < edgeThreshold) {
    const wrappedEntry = [...crossings].sort(
      (left, right) => right.timecodeMs - left.timecodeMs,
    )[0];
    if (wrappedEntry && after) return { entry: wrappedEntry, exit: after };
  }
  if (apex.progress >= gps.length - edgeThreshold) {
    const wrappedExit = [...crossings].sort((left, right) => left.timecodeMs - right.timecodeMs)[0];
    if (before && wrappedExit) return { entry: before, exit: wrappedExit };
  }
  return null;
}

function boundaryCandidates(
  gps: MyChronGpsPoint[],
  line: TrackContributionGpsCoord[],
  requireCrossings: boolean,
): BoundaryCrossing[] {
  const nearestIndex = nearestLineIndex(gps, line);
  const crossings = boundaryCrossings(gps, line, requireCrossings);
  return crossings.length > 0 || requireCrossings
    ? crossings
    : [
        {
          index: nearestIndex,
          progress: nearestIndex,
          timecodeMs: gps[nearestIndex]!.timecodeMs,
          coordinate: { lat: gps[nearestIndex]!.lat, lon: gps[nearestIndex]!.lon },
        },
      ];
}

function forwardProgress(from: number, to: number, period: number) {
  return (to - from + period) % period;
}

function resolveTurnBoundaryPair(
  gps: MyChronGpsPoint[],
  entryLine: TrackContributionGpsCoord[],
  exitLine: TrackContributionGpsCoord[],
  apexProgress: number,
  requireCrossings: boolean,
) {
  const period = gps.length;
  const orientations = [
    {
      entries: boundaryCandidates(gps, entryLine, requireCrossings),
      exits: boundaryCandidates(gps, exitLine, requireCrossings),
      swapped: false,
    },
    {
      entries: boundaryCandidates(gps, exitLine, requireCrossings),
      exits: boundaryCandidates(gps, entryLine, requireCrossings),
      swapped: true,
    },
  ];
  const candidates = orientations.flatMap((orientation) =>
    orientation.entries.flatMap((entry) =>
      orientation.exits.map((exit) => {
        const span = forwardProgress(entry.progress, exit.progress, period);
        const apexOffset = forwardProgress(entry.progress, apexProgress, period);
        return { entry, exit, span, apexOffset, swapped: orientation.swapped };
      }),
    ),
  );
  return (
    candidates
      .filter(
        (candidate) =>
          candidate.span > 0 &&
          candidate.span <= period * 0.5 &&
          candidate.apexOffset <= candidate.span,
      )
      .sort((left, right) => left.span - right.span)[0] ?? null
  );
}

function turnNumber(feature: DetailedCircuitFeature, fallback: number) {
  return Number(feature.label.match(/\d+/)?.[0] ?? fallback);
}

export function matchDetailedTurnPhases(
  lapGps: MyChronGpsPoint[],
  geometry: DetailedCircuitGeometry,
  options: { requireBoundaryCrossings?: boolean } = {},
) {
  if (lapGps.length < 3) {
    return [];
  }
  const alignedGeometry = alignDetailedTurnBoundaries(geometry);
  return alignedGeometry.features
    .filter(
      (feature) =>
        feature.type.toLowerCase() === 'corner' &&
        (featureBoundaryRings(feature).length > 0 ||
          (Boolean(lineMidpoint(feature.entryLine)) && Boolean(lineMidpoint(feature.exitLine)))),
    )
    .map((feature, index): TurnPhaseMatch | null => {
      const apex = deriveGeometricApexPoint(alignedGeometry, feature);
      const hasArea = featureBoundaryRings(feature).length > 0;
      if (!apex || (!hasArea && (!feature.entryLine?.length || !feature.exitLine?.length))) {
        return null;
      }
      const apexProjection = projectPointOntoGps(lapGps, apex);
      const hasExplicitBoundaries = Boolean(feature.entryLine?.length && feature.exitLine?.length);
      const linePair = hasExplicitBoundaries
        ? resolveTurnBoundaryPair(
            lapGps,
            feature.entryLine ?? [],
            feature.exitLine ?? [],
            apexProjection.progress,
            options.requireBoundaryCrossings ?? false,
          )
        : null;
      const areaCrossings =
        !hasExplicitBoundaries && hasArea
          ? resolveFeatureAreaCrossings(lapGps, feature, apexProjection, options.requireBoundaryCrossings ?? false)
          : null;
      const entryCrossing = linePair?.entry ?? areaCrossings?.entry ?? null;
      const exitCrossing = linePair?.exit ?? areaCrossings?.exit ?? null;
      if (!entryCrossing || !exitCrossing) return null;
      const wrapsLap = exitCrossing.progress < entryCrossing.progress;
      const entryDistanceM = linePair
        ? distanceToLineMeters(
            entryCrossing.coordinate,
            linePair.swapped ? (feature.exitLine ?? []) : (feature.entryLine ?? []),
          )
        : 0;
      const exitDistanceM = linePair
        ? distanceToLineMeters(
            exitCrossing.coordinate,
            linePair.swapped ? (feature.entryLine ?? []) : (feature.exitLine ?? []),
          )
        : 0;
      const apexDistanceM = distanceBetweenMeters(apexProjection.coordinate, apex);
      const apexAreaDistanceM = hasArea ? distanceToFeatureAreaMeters(apex, feature) : 0;
      const entryAreaDistanceM = hasArea
        ? distanceToFeatureAreaMeters(entryCrossing.coordinate, feature)
        : 0;
      const exitAreaDistanceM = hasArea
        ? distanceToFeatureAreaMeters(exitCrossing.coordinate, feature)
        : 0;
      const maxDistanceM = TURN_TRAVERSE_MAX_DISTANCE_M;

      if (
        entryDistanceM > maxDistanceM ||
        exitDistanceM > maxDistanceM ||
        apexDistanceM > maxDistanceM ||
        apexAreaDistanceM > maxDistanceM ||
        entryAreaDistanceM > maxDistanceM ||
        exitAreaDistanceM > maxDistanceM
      ) {
        return null;
      }

      return {
        featureId: feature.id,
        label: feature.label,
        turnNumber: turnNumber(feature, index + 1),
        entryMs: entryCrossing.timecodeMs,
        apexMs: apexProjection.timecodeMs,
        exitMs: exitCrossing.timecodeMs,
        wrapsLap,
        boundariesSwapped: linePair?.swapped ?? false,
      };
    })
    .filter((match): match is TurnPhaseMatch => match !== null)
    .sort((left, right) => left.turnNumber - right.turnNumber);
}
